import {useEffect, useMemo, useState} from 'react';

import {oneOfType, node, func} from 'prop-types';

import {API_ENDPOINTS, SNACKBAR_ACTIONS} from '../const';
import BudgetContext from '../contexts/BudgetContext';
import useHttp from '../hooks/misc/useHttp';
import useReport from '../hooks/providers/useReport';
import useSnackbar from '../hooks/providers/useSnackbar';
import {downloadFileFromApiRawResult, getDistinctBudgetYearsFromData} from '../utils';

// eslint-disable-next-line complexity
const BudgetProvider = ({children}) => {
  const {_post} = useHttp();
  const {showSnackbar, closeSnackbar, defaultSnackbarOptions} = useSnackbar();
  const {selectedProject, selectedReport} = useReport();

  const [currentProjectBudgets, setCurrentProjectBudgets] = useState([]);
  const [createBudgetError, setCreateBudgetError] = useState('');
  const [allBudgetsYears, setAllBudgetsYears] = useState([]);
  const [firstFiscalMonth, setFirstFiscalMonth] = useState(null);
  const [isCreatingOrUpdatingBudget, setIsCreatingOrUpdatingBudget] = useState(false);
  const [isCreateBudgetForm, setIsCreateBudgetForm] = useState(false);
  const [spreadsheetDataLoaded, setSpreadsheetDataLoaded] = useState(false);
  const [selectedBudget, setSelectedBudget] = useState(null);
  const [selectedBudgetYears, setSelectedBudgetYears] = useState([]);
  const [isExportingBudgetToFile, setIsExportingBudgetToFile] = useState(false);
  const [isImportingBudgetFromFile, setIsImportingBudgetFromFile] = useState(false);
  const [isImportBudgetModalOpen, setIsImportBudgetModalOpen] = useState(false);
  const [isExportBudgetModalOpen, setIsExportBudgetModalOpen] = useState(false);
  const [isCreateOrUpdateBudgetFormOpen, setIsCreateOrUpdateBudgetFormOpen] = useState(false);
  const [isDeleteBudgetDialogOpen, setIsDeleteBudgetDialogOpen] = useState(false);
  const [alreadyTakenBudgetNames, setAlreadyTakenBudgetNames] = useState([]);
  const [spreadsheetMenuOpen, setSpreadsheetMenuOpen] = useState(false);
  const [isSpreadsheetModalOpen, setIsSpreadsheetModalOpen] = useState(false);
  const [autoSaveDisabled, setAutoSaveDisabled] = useState(JSON.parse(localStorage.getItem('autoSaveDisabled')));
  const [isDeletingBudget, setIsDeletingBudget] = useState(false);

  useEffect(() => {
    if (selectedProject) {
      console.log('Will setCurrentProjectBudgets , allBudgetsYears: ', allBudgetsYears);
      console.log({selectedProject});
      setCurrentProjectBudgets(allBudgetsYears.filter(b => b.siren === selectedProject.siren));
    }
  }, [selectedProject, allBudgetsYears]);

  useEffect(() => {
    const budgetYears = getDistinctBudgetYearsFromData(allBudgetsYears, selectedBudget);
    setSelectedBudgetYears(budgetYears);
  }, [selectedBudget, allBudgetsYears]);

  const enableOrDisableAutoSave = () => {
    localStorage.setItem('autoSaveDisabled', !autoSaveDisabled);
    setAutoSaveDisabled(!autoSaveDisabled);
  };

  // For more info on business need , see : https://github.com/Adrian1903/drivn-powerbi-embed/issues/316
  const createReportBudgetData = async (siren, {budgetName, budgetYear, budgetReferenceYear}) => {
    const url = API_ENDPOINTS.reports.createReportBudgetData;
    setCreateBudgetError('');
    try {
      const {response} = await _post(url, {
        siren,
        budget_name: budgetName,
        budget_year: budgetYear,
        reference_year: budgetReferenceYear
      });

      return {success: response.status === 200, status: response.status};
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      return {success: false};
    }
  };

  const getSpreadsheetData = async (siren, target) => {
    const url = API_ENDPOINTS.reports.getSpreadsheetData;
    setCreateBudgetError('');
    try {
      const {response, responseJson: data} = await _post(url, {
        siren,
        target // 'forecast' or 'budget'
      });

      return {data, status: response.status};
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      return {success: false};
    }
  };

  const getBudgetYears = async siren => {
    const url = API_ENDPOINTS.reports.budget.getBudgetsList;
    try {
      const {response, responseJson: data} = await _post(url, {
        siren
      });

      if (data.length > 0 && response.status === 200) {
        setAllBudgetsYears(data);
        setFirstFiscalMonth(data[0].budget_fy_list[0].slice(5, 7));
      }

      return {data, status: response.status, success: response.status === 200};
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      return {success: false};
    }
  };

  const saveUpdatedSpreadsheetData = async (siren, target, editedData, budget = null) => {
    const url = API_ENDPOINTS.reports.updateSpreadsheetData;
    try {
      const {response, responseJson: data} = await _post(url, {
        siren,
        target, // 'forecast' or 'budget',
        data: editedData,
        budget_name: budget
      });

      if (response.status === 400) {
        showSnackbar(data, {
          severity: 'error',
          duration: 5000
        });
      }

      return {status: response.status};
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      return {success: false};
    }
  };

  const createOrUpdateBudget = async ({budgetName: newBudgetName, budgetYear, budgetReferenceYear, siren}) => {
    setIsCreatingOrUpdatingBudget(true);

    const inProgressSnackbarAction = isCreateBudgetForm ? SNACKBAR_ACTIONS.CREATE_BUDGET_IN_PROGRESS : SNACKBAR_ACTIONS.ADD_FISCAL_YEAR_TO_BUDGET;
    const snackbarId = showSnackbar(inProgressSnackbarAction, {
      severity: 'info',
      autoHide: false
    });

    const result = await createReportBudgetData(siren, {
      budgetName: newBudgetName,
      budgetYear,
      budgetReferenceYear
    });

    closeSnackbar(snackbarId);

    if (result.status === 200) {
      const successSnackbarAction = isCreateBudgetForm ? SNACKBAR_ACTIONS.CREATE_BUDGET_SUCCESS : SNACKBAR_ACTIONS.ADD_FISCAL_YEAR_TO_BUDGET_SUCCESS;
      showSnackbar(successSnackbarAction);
      selectedReport.refresh();
      setSpreadsheetDataLoaded(false);
      setIsCreateOrUpdateBudgetFormOpen(false);
      setSpreadsheetMenuOpen(false);
    }
    setIsCreatingOrUpdatingBudget(false);
  };

  const exportBudgetToFile = async format => {
    const url = API_ENDPOINTS.reports.budget.export;

    setIsExportingBudgetToFile(true);
    const snackbarId = showSnackbar(
      SNACKBAR_ACTIONS.EXPORT_BUDGET_FILE_IN_PROGRESS,
      {
        severity: 'info',
        autoHide: false
      },
      {exportedBudgetName: selectedBudget}
    );

    try {
      const {response, responseJson: data} = await _post(url, {
        siren: selectedProject.siren,
        budget_name: selectedBudget,
        export_type: format
      });

      setIsExportingBudgetToFile(false);
      closeSnackbar(snackbarId);

      if (response.status === 200) {
        showSnackbar(SNACKBAR_ACTIONS.EXPORT_BUDGET_FILE_SUCCESS, defaultSnackbarOptions, {exportedBudgetName: selectedBudget});
        downloadFileFromApiRawResult(data, `${selectedBudget}.${format}`);
      }

      return {data, success: response.status === 200, status: response.status};
    } catch (e) {
      setIsExportingBudgetToFile(false);
      // eslint-disable-next-line no-console
      console.error(e);
      return {success: false};
    }
  };

  const importBudgetFile = async (base64File, format) => {
    const url = API_ENDPOINTS.reports.budget.import;

    setIsImportingBudgetFromFile(true);
    const snackbarId = showSnackbar(SNACKBAR_ACTIONS.IMPORT_BUDGET_FILE_IN_PROGRESS, {
      severity: 'info',
      autoHide: false
    });

    try {
      const {response, responseJson: data} = await _post(url, {
        siren: selectedProject.siren,
        budget_name: selectedBudget,
        import_type: format,
        file_b64: base64File
      });

      setIsImportingBudgetFromFile(false);
      closeSnackbar(snackbarId);

      if (response.status === 200) {
        await selectedReport.refresh();
        downloadFileFromApiRawResult(data, `${selectedBudget}.${format}`);
        showSnackbar(SNACKBAR_ACTIONS.IMPORT_BUDGET_FILE_SUCCESS);
      } else {
        showSnackbar(data, {
          severity: 'error'
        });
      }

      return {data, success: response.status === 200, status: response.status};
    } catch (e) {
      setIsImportingBudgetFromFile(false);
      // eslint-disable-next-line no-console
      console.error(e);
      return {success: false};
    }
  };

  const deleteBudget = async ({budgetName, budgetYear = null, siren}) => {
    const url = API_ENDPOINTS.reports.deleteReportBudgetData;
    setIsDeletingBudget(true);
    try {
      const snackbarId = showSnackbar(
        SNACKBAR_ACTIONS.DELETE_BUDGET_IN_PROGRESS,
        {
          severity: 'info',
          autoHide: false
        },
        {
          budgetName,
          ...(budgetYear && {budgetYear})
        }
      );

      const {response} = await _post(url, {
        siren,
        budget_name: budgetName,
        ...(budgetYear && {budget_year: parseInt(budgetYear, 10)})
      });

      setIsDeletingBudget(false);
      closeSnackbar(snackbarId);

      if (response.status === 200) {
        showSnackbar(SNACKBAR_ACTIONS.DELETE_BUDGET_SUCCESS);
        selectedReport.refresh();
        setSpreadsheetDataLoaded(false);
      }

      return {success: response.status === 200, status: response.status};
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      return {success: false};
    }
  };

  const useMemoDeps = [
    createBudgetError,
    setCreateBudgetError,
    currentProjectBudgets,
    createReportBudgetData,
    getSpreadsheetData,
    saveUpdatedSpreadsheetData,
    getBudgetYears,
    firstFiscalMonth,
    isCreatingOrUpdatingBudget,
    isCreateBudgetForm,
    setIsCreateBudgetForm,
    spreadsheetDataLoaded,
    setSpreadsheetDataLoaded,
    selectedBudget,
    setSelectedBudget,
    selectedBudgetYears,
    isExportingBudgetToFile,
    isImportingBudgetFromFile,
    isImportBudgetModalOpen,
    setIsImportBudgetModalOpen,
    isExportBudgetModalOpen,
    setIsExportBudgetModalOpen,
    setIsCreateOrUpdateBudgetFormOpen,
    isCreateOrUpdateBudgetFormOpen,
    setIsDeleteBudgetDialogOpen,
    isDeleteBudgetDialogOpen,
    setAlreadyTakenBudgetNames,
    alreadyTakenBudgetNames,
    exportBudgetToFile,
    importBudgetFile,
    deleteBudget,
    spreadsheetMenuOpen,
    setSpreadsheetMenuOpen,
    isSpreadsheetModalOpen,
    setIsSpreadsheetModalOpen,
    enableOrDisableAutoSave,
    autoSaveDisabled,
    createOrUpdateBudget,
    isDeletingBudget
  ];
  const value = useMemo(
    () => ({
      createBudgetError,
      setCreateBudgetError,
      currentProjectBudgets,
      createReportBudgetData,
      getSpreadsheetData,
      saveUpdatedSpreadsheetData,
      getBudgetYears,
      firstFiscalMonth,
      isCreatingOrUpdatingBudget,
      isCreateBudgetForm,
      setIsCreateBudgetForm,
      spreadsheetDataLoaded,
      setSpreadsheetDataLoaded,
      selectedBudget,
      setSelectedBudget,
      selectedBudgetYears,
      isExportingBudgetToFile,
      isImportingBudgetFromFile,
      isImportBudgetModalOpen,
      setIsImportBudgetModalOpen,
      isExportBudgetModalOpen,
      setIsExportBudgetModalOpen,
      setIsCreateOrUpdateBudgetFormOpen,
      isCreateOrUpdateBudgetFormOpen,
      setIsDeleteBudgetDialogOpen,
      isDeleteBudgetDialogOpen,
      setAlreadyTakenBudgetNames,
      alreadyTakenBudgetNames,
      exportBudgetToFile,
      importBudgetFile,
      deleteBudget,
      spreadsheetMenuOpen,
      setSpreadsheetMenuOpen,
      isSpreadsheetModalOpen,
      setIsSpreadsheetModalOpen,
      enableOrDisableAutoSave,
      autoSaveDisabled,
      createOrUpdateBudget,
      isDeletingBudget
    }),
    useMemoDeps
  );

  return <BudgetContext.Provider value={value}>{children}</BudgetContext.Provider>;
};

BudgetProvider.propTypes = {
  children: oneOfType([node, func]).isRequired
};

export default BudgetProvider;
